import { CollectionReference, DocumentReference } from 'firebase/firestore';

/**
 * Parses a Firestore path and extracts the document IDs, keyed by collection name.
 *
 * @param path A Firestore path, either as a string or a Collection/Document reference.
 * @param collections Optional collection names to validate against. Omit to extract all.
 * @returns An object mapping each collection name to its document ID in the path.
 */
export function pathToIds<const T extends readonly string[] = never>(
  path: CollectionReference | DocumentReference | string,
  collections?: T,
): [T] extends [never] ? Record<string, string> : { [K in T[number]]: string } {
  if (path instanceof CollectionReference || path instanceof DocumentReference) {
    path = path.path;
  }

  const result = {} as { [K in T[number]]: string };
  const segments = path.split('/').filter(Boolean);

  if (collections == null) {
    collections = segments.filter((_, index) => index % 2 === 0) as unknown as T;
  }

  for (const collection of collections) {
    const segment = segments.shift();

    if (segment !== collection) {
      throw new Error(`Expected collection "${collection}" but got "${segment}" in path: ${path}`);
    }

    result[collection as T[number]] = segments.shift()!;
  }

  return result;
}
